// src/frontend_react/src/layouts/UserLayout.js
import React, { useState, useEffect, useCallback } from 'react';
import { Outlet } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

// Component Imports
import UserSidebar from '../components/navigation/UserSidebar';
import Stack from '../components/ui/Stack/Stack';
import Box from '../components/ui/Box/Box';
import styles from './Layouts.module.css';

// API services
import * as api from '../services/api';

function UserLayout() {
  const appHeaderHeight = 'var(--app-header-height, 65px)';

  // Load the last selected project ID from localStorage, or default to an empty string.
  const [selectedProjectId, setSelectedProjectId] = useState(() => localStorage.getItem('selectedProjectId') || '');

  // Fetch the list of all active projects.
  const {
    data: projects = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ['projects'],
    queryFn: () => api.fetchProjects().then(data => Array.isArray(data) ? data.filter(p => p.is_active) : []),
  });

  // Callback to allow child components to change the selected project.
  const handleProjectChange = useCallback((newProjectId) => {
    setSelectedProjectId(newProjectId);
    if (newProjectId) {
      localStorage.setItem('selectedProjectId', newProjectId);
    } else {
      localStorage.removeItem('selectedProjectId');
    }
  }, []);

  // This effect ensures a valid project is always selected if possible.
  useEffect(() => {
    if (isLoading || projects.length === 0) return; // Don't do anything if loading or no projects

    const activeProjectIds = projects.map(p => p.id);
    const storedIdIsValid = selectedProjectId && activeProjectIds.includes(selectedProjectId);

    if (!storedIdIsValid) {
      const testProject = projects.find(p => p.id.includes('test'));
      const defaultProjectId = testProject ? testProject.id : projects[0].id;
      handleProjectChange(defaultProjectId);
    }
  }, [isLoading, projects, selectedProjectId, handleProjectChange]);

  const outletContext = {
    selectedProjectId,
    handleProjectChange,
    projects,
    isLoading,
    error: error?.message,
  };

  return (
    <Stack 
      direction="row" 
      className={styles.layoutMain}
      style={{ 
        minHeight: `calc(100vh - ${appHeaderHeight})`,
        height: `calc(100vh - ${appHeaderHeight})`
      }}
    >
      <UserSidebar />
      <Box
        as="main"
        flexGrow={1}
        p="lg"
        className={styles.contentArea}
        style={{ overflowY: 'auto', height: '100%' }} 
      >
        <Outlet context={outletContext} />
      </Box>
    </Stack>
  );
};

export default UserLayout;